/**
 * ====================================================================
 * FIKO ADVANCED DIMENSION SYSTEM
 * ====================================================================
 *
 * Daha önce hiç yapılmamış, muhteşem 3D ölçüm gösterim sistemi.
 * Her durum, her seçime uygun, interaktif ve görsel olarak etkileyici.
 *
 * Özellikler:
 * - Holografik glow efektleri
 * - Akıllı ok sistemleri (kameraya göre otomatik yönlenme)
 * - Bezier/Spline çizgiler
 * - 3D yaylar (açı ölçümleri)
 * - Canlı animasyonlar (pulse, wave, morph)
 * - Trail efektleri (hareket izi)
 * - Akıllı yerleşim (overlap/occlusion önleme)
 * - Sürüklenebilir ve yeniden boyutlandırılabilir
 *
 * @author FikoYT CAD Team
 * @version 3.0.0 - NEXT GEN
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { FikoSahne } from '../../../FikoSahne';
import { getCanvasResolution } from '../../../FikoCanvasResolution';

// ====================================================================
// TYPES & INTERFACES
// ====================================================================

/**
 * Gelişmiş ölçüm tipleri
 */
export enum AdvancedDimensionType {
    LINEAR = 'LINEAR',
    ANGULAR = 'ANGULAR',
    RADIAL = 'RADIAL',
    DIAMETER = 'DIAMETER',
    ARC_LENGTH = 'ARC_LENGTH',
    SURFACE_DISTANCE = 'SURFACE_DISTANCE',  // Yüzey üzerinde mesafe
    CHAIN = 'CHAIN',                        // Zincir ölçü
    BASELINE = 'BASELINE',                  // Baseline ölçü
    ORDINATE = 'ORDINATE',                  // Koordinat ölçü
    SYMMETRIC = 'SYMMETRIC',                // Simetri ölçü
    CHAMFER = 'CHAMFER',                    // Pah ölçü
    FILLET = 'FILLET',                      // Radyus ölçü
    THREAD = 'THREAD',                      // Vida ölçü
    TOLERANCE = 'TOLERANCE',                // Tolerans ölçü
    GEOMETRIC = 'GEOMETRIC'                 // Geometrik tolerans
}

/**
 * Görsel stil temaları
 */
export enum DimensionTheme {
    HOLOGRAPHIC = 'HOLOGRAPHIC',     // Futuristik hologram
    CATIA_PRO = 'CATIA_PRO',         // CATIA profesyonel
    NEON = 'NEON',                   // Neon ışık
    MINIMAL = 'MINIMAL',             // Minimalist
    BLUEPRINT = 'BLUEPRINT',         // Mavi plan
    GOLDEN = 'GOLDEN',               // Altın
    MATRIX = 'MATRIX',               // Matrix yeşil
    FROST = 'FROST',                 // Buz mavisi
    FIRE = 'FIRE',                   // Ateş
    CUSTOM = 'CUSTOM'                // Özel
}

/**
 * Animasyon tipleri
 */
export enum AnimationType {
    NONE = 'NONE',
    FADE = 'FADE',
    PULSE = 'PULSE',
    WAVE = 'WAVE',
    GLOW = 'GLOW',
    MORPH = 'MORPH',
    TRAIL = 'TRAIL',
    SPARKLE = 'SPARKLE',
    ELECTRIC = 'ELECTRIC',
    BREATHING = 'BREATHING'
}

/**
 * Ok stilleri - Genişletilmiş
 */
export enum AdvancedArrowStyle {
    // Klasik
    FILLED = 'FILLED',
    OPEN = 'OPEN',
    DOT = 'DOT',
    TICK = 'TICK',
    // Profesyonel
    CATIA = 'CATIA',
    SOLIDWORKS = 'SOLIDWORKS',
    AUTOCAD = 'AUTOCAD',
    // Futuristik
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    NEON = 'NEON',
    PLASMA = 'PLASMA',
    ENERGY = 'ENERGY',
    // Özel
    DIAMOND = 'DIAMOND',
    STAR = 'STAR',
    CROSS = 'CROSS',
    CHEVRON = 'CHEVRON',
    DOUBLE = 'DOUBLE',
    TRIPLE = 'TRIPLE',
    NONE = 'NONE'
}

/**
 * Çizgi stilleri - Genişletilmiş
 */
export enum AdvancedLineStyle {
    SOLID = 'SOLID',
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',
    DASH_DOT = 'DASH_DOT',
    // Özel
    BEZIER = 'BEZIER',
    SPLINE = 'SPLINE',
    WAVE = 'WAVE',
    ZIGZAG = 'ZIGZAG',
    ELECTRIC = 'ELECTRIC',
    GRADIENT = 'GRADIENT',
    DOUBLE = 'DOUBLE',
    RAILROAD = 'RAILROAD'
}

/**
 * Etiket stilleri
 */
export enum LabelStyle {
    FLOATING = 'FLOATING',
    EMBEDDED = 'EMBEDDED',
    BUBBLE = 'BUBBLE',
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    NEON = 'NEON',
    GLASS = 'GLASS',
    METALLIC = 'METALLIC',
    MINIMAL = 'MINIMAL'
}

/**
 * Ölçüm durumu
 */
export enum DimensionState {
    IDLE = 'IDLE',
    HOVER = 'HOVER',
    SELECTED = 'SELECTED',
    DRAGGING = 'DRAGGING',
    EDITING = 'EDITING',
    ANIMATING = 'ANIMATING',
    HIDDEN = 'HIDDEN',
    LOCKED = 'LOCKED'
}

/**
 * Tema renk paleti
 */
export interface ThemeColorPalette {
    primary: number;
    secondary: number;
    accent: number;
    glow: number;
    text: string;
    background: string;
    border: number;
    highlight: number;
    shadow: number;
    gradient?: [number, number];
}

/**
 * Gelişmiş ölçüm ayarları
 */
export interface AdvancedDimensionOptions {
    // Temel
    type: AdvancedDimensionType;
    theme: DimensionTheme;
    customColors?: Partial<ThemeColorPalette>;

    // Çizgi
    lineStyle: AdvancedLineStyle;
    lineWidth: number;
    lineOpacity: number;

    // Ok
    arrowStyle: AdvancedArrowStyle;
    arrowSize: number;
    arrowsInside: boolean;

    // Etiket
    labelStyle: LabelStyle;
    fontSize: number;
    fontFamily: string;
    showUnit: boolean;
    unit: string;
    precision: number;
    prefix: string;
    suffix: string;

    // Tolerans
    tolerance?: {
        upper?: number;
        lower?: number;
        symmetric?: boolean;
        fit?: string;  // H7/g6 gibi
    };

    // Animasyon
    animation: AnimationType;
    animationSpeed: number;
    animationIntensity: number;

    // Efektler
    glowEnabled: boolean;
    glowIntensity: number;
    glowRadius: number;
    trailEnabled: boolean;
    trailLength: number;
    sparkleEnabled: boolean;

    // Davranış
    draggable: boolean;
    selectable: boolean;
    offset: number;
    autoRotate: boolean;  // Kameraya göre otomatik döndür
    alwaysFaceCamera: boolean;  // Her zaman kameraya baksın

    // Extension lines
    showExtensionLines: boolean;
    extensionLineGap: number;
    extensionLineOvershoot: number;
    extensionLineStyle: AdvancedLineStyle;

    // Render
    depthTest: boolean;
    renderOrder: number;
    castShadow: boolean;
}

// ====================================================================
// TEMA PALETLERI
// ====================================================================

export const THEME_PALETTES: Record<DimensionTheme, ThemeColorPalette> = {
    [DimensionTheme.HOLOGRAPHIC]: {
        primary: 0x00ffff,
        secondary: 0xff00ff,
        accent: 0x00ff00,
        glow: 0x00ffff,
        text: '#00ffff',
        background: 'rgba(0, 255, 255, 0.1)',
        border: 0x00ffff,
        highlight: 0xffffff,
        shadow: 0x004444,
        gradient: [0x00ffff, 0xff00ff]
    },
    [DimensionTheme.CATIA_PRO]: {
        primary: 0x00aa00,
        secondary: 0x008800,
        accent: 0x00ff00,
        glow: 0x00ff00,
        text: '#00aa00',
        background: 'rgba(255, 255, 255, 0.95)',
        border: 0x00aa00,
        highlight: 0x00ff00,
        shadow: 0x004400,
        gradient: [0x00aa00, 0x00ff00]
    },
    [DimensionTheme.NEON]: {
        primary: 0xff0080,
        secondary: 0x8000ff,
        accent: 0x00ffff,
        glow: 0xff0080,
        text: '#ff0080',
        background: 'rgba(0, 0, 0, 0.8)',
        border: 0xff0080,
        highlight: 0xffffff,
        shadow: 0x400040,
        gradient: [0xff0080, 0x00ffff]
    },
    [DimensionTheme.MINIMAL]: {
        primary: 0x333333,
        secondary: 0x666666,
        accent: 0x0066cc,
        glow: 0x0066cc,
        text: '#333333',
        background: 'transparent',
        border: 0x333333,
        highlight: 0x0066cc,
        shadow: 0x111111
    },
    [DimensionTheme.BLUEPRINT]: {
        primary: 0x4169e1,
        secondary: 0x1e90ff,
        accent: 0xffffff,
        glow: 0x4169e1,
        text: '#4169e1',
        background: 'rgba(30, 60, 114, 0.1)',
        border: 0x4169e1,
        highlight: 0xffffff,
        shadow: 0x1e3c72,
        gradient: [0x1e3c72, 0x2a5298]
    },
    [DimensionTheme.GOLDEN]: {
        primary: 0xffd700,
        secondary: 0xffa500,
        accent: 0xffff00,
        glow: 0xffd700,
        text: '#ffd700',
        background: 'rgba(255, 215, 0, 0.1)',
        border: 0xffd700,
        highlight: 0xffffff,
        shadow: 0x8b6914,
        gradient: [0xffd700, 0xffa500]
    },
    [DimensionTheme.MATRIX]: {
        primary: 0x00ff00,
        secondary: 0x008800,
        accent: 0x00ff00,
        glow: 0x00ff00,
        text: '#00ff00',
        background: 'rgba(0, 0, 0, 0.9)',
        border: 0x00ff00,
        highlight: 0x00ff00,
        shadow: 0x003300,
        gradient: [0x003300, 0x00ff00]
    },
    [DimensionTheme.FROST]: {
        primary: 0x87ceeb,
        secondary: 0xadd8e6,
        accent: 0xe0ffff,
        glow: 0x87ceeb,
        text: '#87ceeb',
        background: 'rgba(135, 206, 235, 0.1)',
        border: 0x87ceeb,
        highlight: 0xffffff,
        shadow: 0x4682b4,
        gradient: [0x87ceeb, 0xe0ffff]
    },
    [DimensionTheme.FIRE]: {
        primary: 0xff4500,
        secondary: 0xff6347,
        accent: 0xffd700,
        glow: 0xff4500,
        text: '#ff4500',
        background: 'rgba(255, 69, 0, 0.1)',
        border: 0xff4500,
        highlight: 0xffd700,
        shadow: 0x8b0000,
        gradient: [0xff4500, 0xffd700]
    },
    [DimensionTheme.CUSTOM]: {
        primary: 0x000000,
        secondary: 0x333333,
        accent: 0x666666,
        glow: 0x000000,
        text: '#000000',
        background: 'transparent',
        border: 0x000000,
        highlight: 0x333333,
        shadow: 0x000000
    }
};

// ====================================================================
// VARSAYILAN AYARLAR
// ====================================================================

export const DEFAULT_ADVANCED_OPTIONS: AdvancedDimensionOptions = {
    type: AdvancedDimensionType.LINEAR,
    theme: DimensionTheme.HOLOGRAPHIC,

    lineStyle: AdvancedLineStyle.SOLID,
    lineWidth: 2,
    lineOpacity: 1,

    arrowStyle: AdvancedArrowStyle.HOLOGRAPHIC,
    arrowSize: 1,
    arrowsInside: true,

    labelStyle: LabelStyle.HOLOGRAPHIC,
    fontSize: 14,
    fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
    showUnit: true,
    unit: 'mm',
    precision: 2,
    prefix: '',
    suffix: '',

    animation: AnimationType.PULSE,
    animationSpeed: 1,
    animationIntensity: 0.5,

    glowEnabled: true,
    glowIntensity: 0.8,
    glowRadius: 2,
    trailEnabled: false,
    trailLength: 5,
    sparkleEnabled: false,

    draggable: true,
    selectable: true,
    offset: 15,
    autoRotate: true,
    alwaysFaceCamera: false,

    showExtensionLines: true,
    extensionLineGap: 2,
    extensionLineOvershoot: 3,
    extensionLineStyle: AdvancedLineStyle.DASHED,

    depthTest: false,
    renderOrder: 10000,
    castShadow: false
};

// ====================================================================
// MAIN CLASS
// ====================================================================

/**
 * FikoAdvancedDimension - Next-Gen 3D Ölçüm Sistemi
 */
export class FikoAdvancedDimension extends THREE.Group {
    // Tip tanımı
    readonly type = 'FikoAdvancedDimension';

    // Ayarlar
    protected options: AdvancedDimensionOptions;
    protected palette: ThemeColorPalette;

    // Sahne referansı
    protected scene: FikoSahne;

    // Durum
    protected _state: DimensionState = DimensionState.IDLE;
    protected _value: number = 0;

    // Noktalar
    protected pointA: THREE.Vector3 = new THREE.Vector3();
    protected pointB: THREE.Vector3 = new THREE.Vector3();
    protected offsetDirection: THREE.Vector3 = new THREE.Vector3();

    // Çizim elemanları
    protected mainLine: Line2 | null = null;
    protected extensionLine1: Line2 | null = null;
    protected extensionLine2: Line2 | null = null;
    protected arrow1: THREE.Group | null = null;
    protected arrow2: THREE.Group | null = null;
    protected label: THREE.Sprite | null = null;
    protected glowMesh: THREE.Mesh | null = null;
    protected trailGroup: THREE.Group | null = null;

    // Animasyon
    protected animationFrame: number | null = null;
    protected animationTime: number = 0;

    // Event handlers
    protected cameraChangeHandler: (() => void) | null = null;

    // Performans için static buffer'lar
    private static readonly _tempVec1 = new THREE.Vector3();
    private static readonly _tempVec2 = new THREE.Vector3();
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempQuat = new THREE.Quaternion();
    private static readonly _tempMatrix = new THREE.Matrix4();

    // ====================================================================
    // CONSTRUCTOR
    // ====================================================================

    constructor(
        scene: FikoSahne,
        pointA: THREE.Vector3,
        pointB: THREE.Vector3,
        options: Partial<AdvancedDimensionOptions> = {}
    ) {
        super();

        this.scene = scene;
        this.pointA.copy(pointA);
        this.pointB.copy(pointB);

        // Ayarları birleştir
        this.options = { ...DEFAULT_ADVANCED_OPTIONS, ...options };

        // Temayı ayarla
        this.palette = this.options.customColors
            ? { ...THEME_PALETTES[this.options.theme], ...this.options.customColors }
            : { ...THEME_PALETTES[this.options.theme] };

        // Render ayarları
        this.renderOrder = this.options.renderOrder;
        this.frustumCulled = false;

        // Değeri hesapla
        this._value = this.calculateValue();

        // Geometriyi oluştur
        this.build();

        // Kamera değişikliği dinleyicisi
        this.setupCameraListener();

        // Animasyonu başlat
        if (this.options.animation !== AnimationType.NONE) {
            this.startAnimation();
        }
    }

    // ====================================================================
    // GETTERS & SETTERS
    // ====================================================================

    get value(): number {
        return this._value;
    }

    get state(): DimensionState {
        return this._state;
    }

    set state(value: DimensionState) {
        if (this._state !== value) {
            const oldState = this._state;
            this._state = value;
            this.onStateChange(oldState, value);
        }
    }

    // ====================================================================
    // BUILD METHODS
    // ====================================================================

    /**
     * Ana yapıyı oluştur
     */
    protected build(): void {
        // Offset yönünü hesapla
        this.calculateOffsetDirection();

        // Ana çizgiyi oluştur
        this.buildMainLine();

        // Extension line'ları oluştur
        if (this.options.showExtensionLines) {
            this.buildExtensionLines();
        }

        // Okları oluştur
        this.buildArrows();

        // Etiketi oluştur
        this.buildLabel();

        // Glow efektini oluştur
        if (this.options.glowEnabled) {
            this.buildGlow();
        }

        // Trail efektini oluştur
        if (this.options.trailEnabled) {
            this.buildTrail();
        }
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        return this.pointA.distanceTo(this.pointB);
    }

    /**
     * Offset yönünü hesapla (kameraya göre akıllı)
     */
    protected calculateOffsetDirection(): void {
        const direction = FikoAdvancedDimension._tempVec1
            .subVectors(this.pointB, this.pointA)
            .normalize();

        if (this.scene?.anaGosterim?.aktifKamera) {
            const camera = this.scene.anaGosterim.aktifKamera;
            const midPoint = FikoAdvancedDimension._tempVec2
                .addVectors(this.pointA, this.pointB)
                .multiplyScalar(0.5);

            const toCamera = FikoAdvancedDimension._tempVec3
                .subVectors(camera.position, midPoint)
                .normalize();

            // Çizgiye ve kameraya dik
            this.offsetDirection.crossVectors(direction, toCamera).normalize();

            // Çok küçükse (paralel durumu)
            if (this.offsetDirection.lengthSq() < 0.01) {
                this.offsetDirection.crossVectors(direction, camera.up).normalize();
            }

            // Her zaman yukarı doğru
            if (this.offsetDirection.y < 0) {
                this.offsetDirection.negate();
            }
        } else {
            // Fallback
            this.offsetDirection.set(0, 1, 0);
            if (Math.abs(direction.dot(this.offsetDirection)) > 0.9) {
                this.offsetDirection.set(1, 0, 0);
            }
            this.offsetDirection.crossVectors(direction, this.offsetDirection).normalize();
        }
    }

    /**
     * Ana çizgiyi oluştur
     */
    protected buildMainLine(): void {
        if (this.mainLine) {
            this.remove(this.mainLine);
            this.mainLine.geometry.dispose();
            (this.mainLine.material as LineMaterial).dispose();
        }

        const offset = this.options.offset;

        // Offset noktaları
        const offsetA = FikoAdvancedDimension._tempVec1
            .copy(this.pointA)
            .addScaledVector(this.offsetDirection, offset);

        const offsetB = FikoAdvancedDimension._tempVec2
            .copy(this.pointB)
            .addScaledVector(this.offsetDirection, offset);

        // Çizgi pozisyonları
        let positions: number[];

        switch (this.options.lineStyle) {
            case AdvancedLineStyle.BEZIER:
                positions = this.createBezierPositions(offsetA, offsetB);
                break;
            case AdvancedLineStyle.SPLINE:
                positions = this.createSplinePositions(offsetA, offsetB);
                break;
            case AdvancedLineStyle.WAVE:
                positions = this.createWavePositions(offsetA, offsetB);
                break;
            default:
                positions = [
                    offsetA.x, offsetA.y, offsetA.z,
                    offsetB.x, offsetB.y, offsetB.z
                ];
        }

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.palette.primary,
            linewidth: this.options.lineWidth,
            transparent: true,
            opacity: this.options.lineOpacity,
            depthTest: this.options.depthTest,
            depthWrite: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const _res1 = getCanvasResolution(this.scene?.anaGosterim?.sunucu);
        material.resolution.set(_res1.width, _res1.height);

        // Dashed line için
        if (this.options.lineStyle === AdvancedLineStyle.DASHED) {
            material.dashed = true;
            material.dashScale = 1;
            material.dashSize = 3;
            material.gapSize = 2;
        } else if (this.options.lineStyle === AdvancedLineStyle.DOTTED) {
            material.dashed = true;
            material.dashScale = 1;
            material.dashSize = 0.5;
            material.gapSize = 1;
        }

        this.mainLine = new Line2(geometry, material);
        this.mainLine.renderOrder = this.options.renderOrder;
        this.mainLine.frustumCulled = false;
        this.mainLine.computeLineDistances();

        this.add(this.mainLine);
    }

    /**
     * Bezier çizgi pozisyonları oluştur
     */
    protected createBezierPositions(start: THREE.Vector3, end: THREE.Vector3): number[] {
        const positions: number[] = [];
        const segments = 32;

        // Kontrol noktaları
        const mid = new THREE.Vector3().lerpVectors(start, end, 0.5);
        const curveHeight = this._value * 0.1;
        mid.addScaledVector(this.offsetDirection, curveHeight);

        // Quadratic Bezier
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const t2 = t * t;
            const mt = 1 - t;
            const mt2 = mt * mt;

            const x = mt2 * start.x + 2 * mt * t * mid.x + t2 * end.x;
            const y = mt2 * start.y + 2 * mt * t * mid.y + t2 * end.y;
            const z = mt2 * start.z + 2 * mt * t * mid.z + t2 * end.z;

            positions.push(x, y, z);
        }

        return positions;
    }

    /**
     * Spline çizgi pozisyonları oluştur
     */
    protected createSplinePositions(start: THREE.Vector3, end: THREE.Vector3): number[] {
        const positions: number[] = [];
        const segments = 48;

        // Kontrol noktaları
        const p1 = new THREE.Vector3().lerpVectors(start, end, 0.25);
        const p2 = new THREE.Vector3().lerpVectors(start, end, 0.75);

        // Dalga efekti
        const waveHeight = this._value * 0.05;
        p1.addScaledVector(this.offsetDirection, waveHeight);
        p2.addScaledVector(this.offsetDirection, -waveHeight);

        // Catmull-Rom spline
        const curve = new THREE.CatmullRomCurve3([start, p1, p2, end]);
        const points = curve.getPoints(segments);

        points.forEach(p => {
            positions.push(p.x, p.y, p.z);
        });

        return positions;
    }

    /**
     * Wave çizgi pozisyonları oluştur
     */
    protected createWavePositions(start: THREE.Vector3, end: THREE.Vector3): number[] {
        const positions: number[] = [];
        const segments = 64;
        const waveCount = 4;
        const waveHeight = this._value * 0.02;

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const point = new THREE.Vector3().lerpVectors(start, end, t);

            // Sinüs dalgası
            const wave = Math.sin(t * Math.PI * 2 * waveCount) * waveHeight;
            point.addScaledVector(this.offsetDirection, wave);

            positions.push(point.x, point.y, point.z);
        }

        return positions;
    }

    /**
     * Extension line'ları oluştur
     */
    protected buildExtensionLines(): void {
        const offset = this.options.offset;
        const gap = this.options.extensionLineGap;
        const overshoot = this.options.extensionLineOvershoot;

        // Extension line 1 (pointA'dan)
        const ext1Start = FikoAdvancedDimension._tempVec1
            .copy(this.pointA)
            .addScaledVector(this.offsetDirection, gap);

        const ext1End = FikoAdvancedDimension._tempVec2
            .copy(this.pointA)
            .addScaledVector(this.offsetDirection, offset + overshoot);

        this.extensionLine1 = this.createExtensionLine(ext1Start, ext1End);
        this.add(this.extensionLine1);

        // Extension line 2 (pointB'den)
        const ext2Start = ext1Start
            .copy(this.pointB)
            .addScaledVector(this.offsetDirection, gap);

        const ext2End = ext1End
            .copy(this.pointB)
            .addScaledVector(this.offsetDirection, offset + overshoot);

        this.extensionLine2 = this.createExtensionLine(ext2Start, ext2End);
        this.add(this.extensionLine2);
    }

    /**
     * Extension line oluştur (yardımcı)
     */
    protected createExtensionLine(start: THREE.Vector3, end: THREE.Vector3): Line2 {
        const geometry = new LineGeometry();
        geometry.setPositions([
            start.x, start.y, start.z,
            end.x, end.y, end.z
        ]);

        const material = new LineMaterial({
            color: this.palette.secondary,
            linewidth: this.options.lineWidth * 0.6,
            transparent: true,
            opacity: this.options.lineOpacity * 0.7,
            depthTest: this.options.depthTest,
            depthWrite: false,
            dashed: this.options.extensionLineStyle === AdvancedLineStyle.DASHED,
            dashScale: 1,
            dashSize: 2,
            gapSize: 1,
            worldUnits: false
        });

        const _res2 = getCanvasResolution(this.scene?.anaGosterim?.sunucu);
        material.resolution.set(_res2.width, _res2.height);

        const line = new Line2(geometry, material);
        line.renderOrder = this.options.renderOrder - 1;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Okları oluştur
     */
    protected buildArrows(): void {
        const offset = this.options.offset;
        const direction = FikoAdvancedDimension._tempVec1
            .subVectors(this.pointB, this.pointA)
            .normalize();

        // Offset noktaları
        const offsetA = FikoAdvancedDimension._tempVec2
            .copy(this.pointA)
            .addScaledVector(this.offsetDirection, offset);

        const offsetB = FikoAdvancedDimension._tempVec3
            .copy(this.pointB)
            .addScaledVector(this.offsetDirection, offset);

        // Ok 1 (A tarafında, B'ye bakıyor)
        this.arrow1 = this.createAdvancedArrow(offsetA.clone(), direction.clone());
        this.add(this.arrow1);

        // Ok 2 (B tarafında, A'ya bakıyor)
        this.arrow2 = this.createAdvancedArrow(offsetB.clone(), direction.clone().negate());
        this.add(this.arrow2);
    }

    /**
     * Gelişmiş ok oluştur
     */
    protected createAdvancedArrow(position: THREE.Vector3, direction: THREE.Vector3): THREE.Group {
        const group = new THREE.Group();
        const size = this.options.arrowSize;

        switch (this.options.arrowStyle) {
            case AdvancedArrowStyle.HOLOGRAPHIC:
                this.createHolographicArrow(group, size);
                break;
            case AdvancedArrowStyle.NEON:
                this.createNeonArrow(group, size);
                break;
            case AdvancedArrowStyle.PLASMA:
                this.createPlasmaArrow(group, size);
                break;
            case AdvancedArrowStyle.ENERGY:
                this.createEnergyArrow(group, size);
                break;
            case AdvancedArrowStyle.DIAMOND:
                this.createDiamondArrow(group, size);
                break;
            case AdvancedArrowStyle.STAR:
                this.createStarArrow(group, size);
                break;
            case AdvancedArrowStyle.CHEVRON:
                this.createChevronArrow(group, size);
                break;
            case AdvancedArrowStyle.DOUBLE:
                this.createDoubleArrow(group, size);
                break;
            case AdvancedArrowStyle.CATIA:
            case AdvancedArrowStyle.SOLIDWORKS:
            case AdvancedArrowStyle.FILLED:
            default:
                this.createFilledArrow(group, size);
                break;
        }

        // Pozisyon ve yön
        group.position.copy(position);

        // Yönlendir
        const quaternion = FikoAdvancedDimension._tempQuat;
        quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), direction);
        group.quaternion.copy(quaternion);

        group.renderOrder = this.options.renderOrder + 1;

        return group;
    }

    /**
     * Holografik ok oluştur
     */
    protected createHolographicArrow(group: THREE.Group, size: number): void {
        // Ana ok
        const coneGeometry = new THREE.ConeGeometry(size * 0.3, size, 6);
        coneGeometry.rotateZ(-Math.PI / 2);

        const coneMaterial = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.8,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest,
            depthWrite: false
        });

        const cone = new THREE.Mesh(coneGeometry, coneMaterial);
        cone.position.x = size * 0.5;
        group.add(cone);

        // Glow ring
        const ringGeometry = new THREE.RingGeometry(size * 0.35, size * 0.45, 16);
        ringGeometry.rotateY(Math.PI / 2);

        const ringMaterial = new THREE.MeshBasicMaterial({
            color: this.palette.glow,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.position.x = size * 0.1;
        group.add(ring);
    }

    /**
     * Neon ok oluştur
     */
    protected createNeonArrow(group: THREE.Group, size: number): void {
        // Neon V şekli
        const shape = new THREE.Shape();
        const w = size * 0.4;
        const h = size;

        shape.moveTo(h, 0);
        shape.lineTo(0, w);
        shape.lineTo(h * 0.3, 0);
        shape.lineTo(0, -w);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);

        // Glow efekti için ikinci katman
        const glowGeometry = geometry.clone();
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: this.palette.glow,
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide,
            depthTest: false
        });

        const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        glowMesh.scale.setScalar(1.3);
        group.add(glowMesh);
    }

    /**
     * Plazma ok oluştur
     */
    protected createPlasmaArrow(group: THREE.Group, size: number): void {
        // İç çekirdek
        const coreGeometry = new THREE.SphereGeometry(size * 0.2, 16, 16);
        const coreMaterial = new THREE.MeshBasicMaterial({
            color: this.palette.highlight,
            transparent: true,
            opacity: 1,
            depthTest: this.options.depthTest
        });

        const core = new THREE.Mesh(coreGeometry, coreMaterial);
        core.position.x = size * 0.3;
        group.add(core);

        // Plazma halkası
        const torusGeometry = new THREE.TorusGeometry(size * 0.35, size * 0.05, 8, 16);
        torusGeometry.rotateY(Math.PI / 2);

        const torusMaterial = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.7,
            depthTest: false
        });

        const torus = new THREE.Mesh(torusGeometry, torusMaterial);
        torus.position.x = size * 0.3;
        group.add(torus);
    }

    /**
     * Enerji ok oluştur
     */
    protected createEnergyArrow(group: THREE.Group, size: number): void {
        // Yıldırım şekli
        const shape = new THREE.Shape();

        shape.moveTo(size, 0);
        shape.lineTo(size * 0.3, size * 0.15);
        shape.lineTo(size * 0.5, size * 0.05);
        shape.lineTo(-size * 0.2, size * 0.2);
        shape.lineTo(size * 0.1, 0);
        shape.lineTo(-size * 0.2, -size * 0.2);
        shape.lineTo(size * 0.5, -size * 0.05);
        shape.lineTo(size * 0.3, -size * 0.15);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.accent,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);
    }

    /**
     * Elmas ok oluştur
     */
    protected createDiamondArrow(group: THREE.Group, size: number): void {
        const shape = new THREE.Shape();

        shape.moveTo(size, 0);
        shape.lineTo(0, size * 0.3);
        shape.lineTo(-size * 0.3, 0);
        shape.lineTo(0, -size * 0.3);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);
    }

    /**
     * Yıldız ok oluştur
     */
    protected createStarArrow(group: THREE.Group, size: number): void {
        const shape = new THREE.Shape();
        const points = 5;
        const outerRadius = size * 0.4;
        const innerRadius = size * 0.2;

        for (let i = 0; i < points * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / points - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            if (i === 0) {
                shape.moveTo(x, y);
            } else {
                shape.lineTo(x, y);
            }
        }
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.accent,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.x = size * 0.3;
        group.add(mesh);
    }

    /**
     * Chevron ok oluştur
     */
    protected createChevronArrow(group: THREE.Group, size: number): void {
        const shape = new THREE.Shape();
        const w = size * 0.35;
        const h = size;
        const t = size * 0.15;

        shape.moveTo(h, 0);
        shape.lineTo(h - t, w);
        shape.lineTo(0, w);
        shape.lineTo(t, 0);
        shape.lineTo(0, -w);
        shape.lineTo(h - t, -w);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);
    }

    /**
     * Çift ok oluştur
     */
    protected createDoubleArrow(group: THREE.Group, size: number): void {
        const createArrowShape = (offset: number): THREE.Shape => {
            const shape = new THREE.Shape();
            const w = size * 0.25;
            const h = size * 0.6;

            shape.moveTo(h + offset, 0);
            shape.lineTo(offset, w);
            shape.lineTo(h * 0.3 + offset, 0);
            shape.lineTo(offset, -w);
            shape.closePath();

            return shape;
        };

        // İlk ok
        const shape1 = createArrowShape(0);
        const geometry1 = new THREE.ShapeGeometry(shape1);
        geometry1.rotateY(Math.PI / 2);

        // İkinci ok
        const shape2 = createArrowShape(-size * 0.5);
        const geometry2 = new THREE.ShapeGeometry(shape2);
        geometry2.rotateY(Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest
        });

        const mesh1 = new THREE.Mesh(geometry1, material);
        const mesh2 = new THREE.Mesh(geometry2, material.clone());

        group.add(mesh1);
        group.add(mesh2);
    }

    /**
     * Dolu ok oluştur (klasik)
     */
    protected createFilledArrow(group: THREE.Group, size: number): void {
        const coneGeometry = new THREE.ConeGeometry(size * 0.3, size, 12);
        coneGeometry.rotateZ(-Math.PI / 2);
        coneGeometry.translate(size * 0.5, 0, 0);

        const material = new THREE.MeshBasicMaterial({
            color: this.palette.primary,
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest,
            depthWrite: false
        });

        const cone = new THREE.Mesh(coneGeometry, material);
        group.add(cone);
    }

    /**
     * Etiketi oluştur
     */
    protected buildLabel(): void {
        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        const text = this.formatValue(this._value);
        const offset = this.options.offset;

        // Etiket pozisyonu
        const midPoint = FikoAdvancedDimension._tempVec1
            .addVectors(this.pointA, this.pointB)
            .multiplyScalar(0.5)
            .addScaledVector(this.offsetDirection, offset);

        this.label = this.createAdvancedLabel(text, midPoint);
        this.add(this.label);
    }

    /**
     * Gelişmiş etiket oluştur
     */
    protected createAdvancedLabel(text: string, position: THREE.Vector3): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const fontSize = this.options.fontSize * 4;
        const padding = 16;

        // Font ayarla
        ctx.font = `bold ${fontSize}px ${this.options.fontFamily}`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize;

        // Canvas boyutu
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Stil'e göre arka plan
        switch (this.options.labelStyle) {
            case LabelStyle.HOLOGRAPHIC:
                this.drawHolographicBackground(ctx, canvas.width, canvas.height);
                break;
            case LabelStyle.NEON:
                this.drawNeonBackground(ctx, canvas.width, canvas.height);
                break;
            case LabelStyle.BUBBLE:
                this.drawBubbleBackground(ctx, canvas.width, canvas.height);
                break;
            case LabelStyle.GLASS:
                this.drawGlassBackground(ctx, canvas.width, canvas.height);
                break;
            case LabelStyle.METALLIC:
                this.drawMetallicBackground(ctx, canvas.width, canvas.height);
                break;
            default:
                // Minimal - arka plan yok
                break;
        }

        // Metin çiz
        ctx.font = `bold ${fontSize}px ${this.options.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Gölge
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        // Outline
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 4;
        ctx.strokeText(text, canvas.width / 2, canvas.height / 2);

        // Metin
        ctx.fillStyle = this.palette.text;
        ctx.shadowBlur = 0;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        // Texture oluştur
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        // Sprite material
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: this.options.depthTest,
            depthWrite: false,
            sizeAttenuation: true
        });

        // Sprite oluştur
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(position);
        sprite.position.addScaledVector(this.offsetDirection, 3);

        // Scale
        const scale = 0.02;
        sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);

        sprite.renderOrder = this.options.renderOrder + 2;
        sprite.frustumCulled = false;

        // Original scale kaydet
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Holografik arka plan çiz
     */
    protected drawHolographicBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        // Gradient arka plan
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, 'rgba(0, 255, 255, 0.3)');
        gradient.addColorStop(0.5, 'rgba(255, 0, 255, 0.2)');
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0.3)');

        ctx.fillStyle = gradient;
        ctx.roundRect(4, 4, width - 8, height - 8, 8);
        ctx.fill();

        // Glow border
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // İç glow
        ctx.shadowColor = 'rgba(0, 255, 255, 0.5)';
        ctx.shadowBlur = 10;
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.5)';
        ctx.stroke();
    }

    /**
     * Neon arka plan çiz
     */
    protected drawNeonBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        // Koyu arka plan
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.roundRect(4, 4, width - 8, height - 8, 8);
        ctx.fill();

        // Neon border
        ctx.shadowColor = this.palette.text;
        ctx.shadowBlur = 15;
        ctx.strokeStyle = this.palette.text;
        ctx.lineWidth = 3;
        ctx.stroke();

        // İkinci glow
        ctx.shadowBlur = 25;
        ctx.strokeStyle = this.palette.text;
        ctx.lineWidth = 1;
        ctx.stroke();
    }

    /**
     * Bubble arka plan çiz
     */
    protected drawBubbleBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        // Yarı saydam beyaz
        const gradient = ctx.createRadialGradient(
            width / 2, height / 3, 0,
            width / 2, height / 2, height
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
        gradient.addColorStop(1, 'rgba(200, 200, 200, 0.85)');

        ctx.fillStyle = gradient;
        ctx.roundRect(4, 4, width - 8, height - 8, height / 2);
        ctx.fill();

        // İnce border
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 1;
        ctx.stroke();
    }

    /**
     * Cam arka plan çiz
     */
    protected drawGlassBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        // Cam efekti
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.5)');

        ctx.fillStyle = gradient;
        ctx.roundRect(4, 4, width - 8, height - 8, 6);
        ctx.fill();

        // Parlak çizgi (üst)
        const highlightGradient = ctx.createLinearGradient(0, 4, 0, height / 3);
        highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
        highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.fillStyle = highlightGradient;
        ctx.roundRect(8, 6, width - 16, height / 3, 4);
        ctx.fill();
    }

    /**
     * Metalik arka plan çiz
     */
    protected drawMetallicBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        // Metal gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#c0c0c0');
        gradient.addColorStop(0.3, '#e8e8e8');
        gradient.addColorStop(0.5, '#a0a0a0');
        gradient.addColorStop(0.7, '#d0d0d0');
        gradient.addColorStop(1, '#909090');

        ctx.fillStyle = gradient;
        ctx.roundRect(4, 4, width - 8, height - 8, 4);
        ctx.fill();

        // Border
        ctx.strokeStyle = '#707070';
        ctx.lineWidth = 2;
        ctx.stroke();
    }

    /**
     * Glow efektini oluştur
     */
    protected buildGlow(): void {
        // TODO: Shader-based glow implementation
        // Bu basit versiyon, daha sonra shader ile geliştirilecek
    }

    /**
     * Trail efektini oluştur
     */
    protected buildTrail(): void {
        // TODO: Trail implementation
    }

    /**
     * Değeri formatla
     */
    protected formatValue(value: number): string {
        let text = value.toFixed(this.options.precision);

        if (this.options.prefix) {
            text = this.options.prefix + text;
        }

        if (this.options.showUnit && this.options.unit) {
            text += ' ' + this.options.unit;
        }

        if (this.options.suffix) {
            text += this.options.suffix;
        }

        // Tolerans
        if (this.options.tolerance) {
            const { upper, lower, symmetric, fit } = this.options.tolerance;

            if (fit) {
                text += ` ${fit}`;
            } else if (symmetric && upper !== undefined) {
                text += ` ±${upper.toFixed(this.options.precision)}`;
            } else {
                if (upper !== undefined) {
                    text += ` +${upper.toFixed(this.options.precision)}`;
                }
                if (lower !== undefined) {
                    text += ` ${lower.toFixed(this.options.precision)}`;
                }
            }
        }

        return text;
    }

    // ====================================================================
    // ANIMATION
    // ====================================================================

    /**
     * Animasyonu başlat
     */
    protected startAnimation(): void {
        if (this.animationFrame !== null) return;

        const animate = () => {
            this.animationTime += 0.016 * this.options.animationSpeed;
            this.updateAnimation();
            this.animationFrame = requestAnimationFrame(animate);
        };

        this.animationFrame = requestAnimationFrame(animate);
    }

    /**
     * Animasyonu durdur
     */
    protected stopAnimation(): void {
        if (this.animationFrame !== null) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    /**
     * Animasyonu güncelle
     */
    protected updateAnimation(): void {
        const intensity = this.options.animationIntensity;
        const time = this.animationTime;

        switch (this.options.animation) {
            case AnimationType.PULSE:
                this.updatePulseAnimation(time, intensity);
                break;
            case AnimationType.WAVE:
                this.updateWaveAnimation(time, intensity);
                break;
            case AnimationType.GLOW:
                this.updateGlowAnimation(time, intensity);
                break;
            case AnimationType.BREATHING:
                this.updateBreathingAnimation(time, intensity);
                break;
            case AnimationType.SPARKLE:
                this.updateSparkleAnimation(time, intensity);
                break;
            case AnimationType.ELECTRIC:
                this.updateElectricAnimation(time, intensity);
                break;
        }
    }

    /**
     * Pulse animasyonu
     */
    protected updatePulseAnimation(time: number, intensity: number): void {
        const pulse = 1 + Math.sin(time * 3) * intensity * 0.2;

        // Ok scale
        if (this.arrow1) this.arrow1.scale.setScalar(pulse);
        if (this.arrow2) this.arrow2.scale.setScalar(pulse);

        // Çizgi opacity
        if (this.mainLine) {
            const material = this.mainLine.material as LineMaterial;
            material.opacity = this.options.lineOpacity * (0.8 + Math.sin(time * 3) * 0.2);
        }
    }

    /**
     * Wave animasyonu
     */
    protected updateWaveAnimation(time: number, intensity: number): void {
        // Label hareket
        if (this.label) {
            const wave = Math.sin(time * 2) * intensity * 0.5;
            this.label.position.y += wave * 0.01;
        }
    }

    /**
     * Glow animasyonu
     */
    protected updateGlowAnimation(time: number, intensity: number): void {
        const glow = 0.5 + Math.sin(time * 2) * intensity * 0.5;

        // Ok glow
        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                arrow.traverse(child => {
                    if (child instanceof THREE.Mesh) {
                        const material = child.material as THREE.MeshBasicMaterial;
                        if (material.opacity !== undefined) {
                            material.opacity = glow;
                        }
                    }
                });
            }
        });
    }

    /**
     * Nefes alma animasyonu
     */
    protected updateBreathingAnimation(time: number, intensity: number): void {
        const breath = 1 + Math.sin(time * 1.5) * intensity * 0.1;

        // Tüm grup scale
        this.scale.setScalar(breath);
    }

    /**
     * Kıvılcım animasyonu
     */
    protected updateSparkleAnimation(time: number, intensity: number): void {
        // Rastgele parıltı
        const sparkle = Math.random() > 0.9 ? 1.5 : 1;

        if (this.label) {
            const material = this.label.material;
            material.opacity = sparkle * intensity;
        }
    }

    /**
     * Elektrik animasyonu
     */
    protected updateElectricAnimation(time: number, intensity: number): void {
        // Titreşim efekti
        const jitter = (Math.random() - 0.5) * intensity * 0.1;

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                arrow.rotation.z = jitter;
            }
        });
    }

    // ====================================================================
    // EVENT HANDLERS
    // ====================================================================

    /**
     * Kamera değişikliği dinleyicisi kur
     */
    protected setupCameraListener(): void {
        if (!this.scene?.anaGosterim?.aktifKontrol) return;

        this.cameraChangeHandler = () => {
            if (this.options.autoRotate) {
                this.calculateOffsetDirection();
                this.rebuild();
            }
        };

        this.scene.anaGosterim.aktifKontrol.addEventListener('change', this.cameraChangeHandler);
    }

    /**
     * Durum değişikliği
     */
    protected onStateChange(oldState: DimensionState, newState: DimensionState): void {
        switch (newState) {
            case DimensionState.HOVER:
                this.onHover();
                break;
            case DimensionState.SELECTED:
                this.onSelect();
                break;
            case DimensionState.DRAGGING:
                this.onDragStart();
                break;
            case DimensionState.IDLE:
                if (oldState === DimensionState.HOVER) {
                    this.onHoverEnd();
                } else if (oldState === DimensionState.DRAGGING) {
                    this.onDragEnd();
                }
                break;
        }
    }

    /**
     * Hover başlangıcı
     */
    protected onHover(): void {
        // Renk değişimi
        if (this.mainLine) {
            (this.mainLine.material as LineMaterial).color.setHex(this.palette.highlight);
        }

        // Scale artışı
        this.scale.setScalar(1.1);
    }

    /**
     * Hover sonu
     */
    protected onHoverEnd(): void {
        // Renk geri
        if (this.mainLine) {
            (this.mainLine.material as LineMaterial).color.setHex(this.palette.primary);
        }

        // Scale geri
        this.scale.setScalar(1);
    }

    /**
     * Seçim
     */
    protected onSelect(): void {
        // Vurgu rengi
        if (this.mainLine) {
            (this.mainLine.material as LineMaterial).color.setHex(this.palette.accent);
        }
    }

    /**
     * Sürükleme başlangıcı
     */
    protected onDragStart(): void {
        // Opacity azalt
        if (this.mainLine) {
            (this.mainLine.material as LineMaterial).opacity = 0.5;
        }
    }

    /**
     * Sürükleme sonu
     */
    protected onDragEnd(): void {
        // Opacity geri
        if (this.mainLine) {
            (this.mainLine.material as LineMaterial).opacity = this.options.lineOpacity;
        }
    }

    // ====================================================================
    // PUBLIC METHODS
    // ====================================================================

    /**
     * Yeniden oluştur
     */
    rebuild(): void {
        this.clearGeometry();
        this.build();
    }

    /**
     * Temayı değiştir
     */
    setTheme(theme: DimensionTheme, customColors?: Partial<ThemeColorPalette>): void {
        this.options.theme = theme;
        this.palette = customColors
            ? { ...THEME_PALETTES[theme], ...customColors }
            : { ...THEME_PALETTES[theme] };

        this.rebuild();
    }

    /**
     * Animasyon tipini değiştir
     */
    setAnimation(type: AnimationType): void {
        this.options.animation = type;

        if (type === AnimationType.NONE) {
            this.stopAnimation();
        } else {
            this.startAnimation();
        }
    }

    /**
     * Noktaları güncelle
     */
    updatePoints(pointA: THREE.Vector3, pointB: THREE.Vector3): void {
        this.pointA.copy(pointA);
        this.pointB.copy(pointB);
        this._value = this.calculateValue();
        this.rebuild();
    }

    /**
     * Offset'i güncelle
     */
    setOffset(offset: number): void {
        this.options.offset = offset;
        this.rebuild();
    }

    // ====================================================================
    // CLEANUP
    // ====================================================================

    /**
     * Geometriyi temizle
     */
    protected clearGeometry(): void {
        // Main line
        if (this.mainLine) {
            this.remove(this.mainLine);
            this.mainLine.geometry.dispose();
            (this.mainLine.material as LineMaterial).dispose();
            this.mainLine = null;
        }

        // Extension lines
        [this.extensionLine1, this.extensionLine2].forEach(line => {
            if (line) {
                this.remove(line);
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            }
        });
        this.extensionLine1 = null;
        this.extensionLine2 = null;

        // Arrows
        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                this.remove(arrow);
                arrow.traverse(child => {
                    if (child instanceof THREE.Mesh) {
                        child.geometry?.dispose();
                        (child.material as THREE.Material)?.dispose();
                    }
                });
            }
        });
        this.arrow1 = null;
        this.arrow2 = null;

        // Label
        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
            this.label = null;
        }

        // Glow
        if (this.glowMesh) {
            this.remove(this.glowMesh);
            this.glowMesh.geometry?.dispose();
            (this.glowMesh.material as THREE.Material)?.dispose();
            this.glowMesh = null;
        }
    }

    /**
     * Dispose
     */
    dispose(): void {
        // Animasyonu durdur
        this.stopAnimation();

        // Event listener'ı kaldır
        if (this.cameraChangeHandler && this.scene?.anaGosterim?.aktifKontrol) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener('change', this.cameraChangeHandler);
            this.cameraChangeHandler = null;
        }

        // Geometriyi temizle
        this.clearGeometry();

        // Parent'tan kaldır
        this.parent?.remove(this);
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export default FikoAdvancedDimension;
